'use client';

/**
 * The employee portal's Challenges (2026-09-26, audit wave 3 batch B, item 3 —
 * the rest of G16: challenges had no learner UI at all).
 *
 * Every challenge open to the learner now, from GET /v1/portal/challenges/
 * active: its dates, its reward, the learner's progress (counted by the API the
 * way the challenge engine counts it) and an Accept button — accepting is
 * idempotent (one participant per learner). Hidden when the tenant has
 * Gamification off (those routes answer 403 module_disabled) and when nothing
 * is open.
 *
 * In the reader's language (messages/portal, `portal.challenges.*`), like the
 * rest of the portal. A challenge runs from the start of its first day to the
 * end of its last, days in the workspace's zone (locale audit 2026-09-26,
 * FE-DT-14), so each date is shown as that zone's day: the viewer's own zone
 * would put the end of 30 Sep in Dubai on 1 Oct for a learner in India.
 */
import { Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  useAcceptChallenge,
  usePortalActiveChallenges,
} from '@/components/portal/hooks/use-portal-gamification';
import { formatLearnerDay } from '@/components/user-portal/learner-i18n';
import { isModuleDisabledError } from '@/lib/api/module-disabled';
import { useTranslation } from '@/lib/i18n';
import { browserTimeZone } from '@/lib/time/zone';
import { calendarDayOf } from '@/lib/time/zoned-day';
import type { Challenge } from '@/lib/types/gamification';

export function PortalChallengesSection({
  timeZone,
}: {
  /**
   * The workspace's zone, the one the challenge days are in (the portal reads
   * it off the learner's enrolments); the browser's while none is known.
   */
  timeZone?: string;
} = {}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = usePortalActiveChallenges();
  const accept = useAcceptChallenge();

  if (isLoading || isModuleDisabledError(error, 'gamification')) return null;
  const challenges = data ?? [];
  if (challenges.length === 0) return null;

  return (
    <section
      id="portal-section-challenges"
      aria-labelledby="portal-challenges-heading"
      className="py-12 px-8 bg-background"
    >
      <h2 id="portal-challenges-heading" className="page-heading text-foreground mb-6">
        {t('portal.challenges.title')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {challenges.map((challenge) => (
          <ChallengeCard
            key={challenge.id}
            challenge={challenge}
            timeZone={timeZone ?? browserTimeZone()}
            accepting={accept.isPending}
            onAccept={() => accept.mutate(challenge.id)}
          />
        ))}
      </div>
    </section>
  );
}

function ChallengeCard({
  challenge,
  timeZone,
  accepting,
  onAccept,
}: {
  challenge: Challenge;
  timeZone: string;
  accepting: boolean;
  onAccept: () => void;
}) {
  const { t, locale } = useTranslation();
  const standing = challenge.learner ?? { current: 0, target: 1, accepted: false };
  const completed = !!challenge.completion;
  // The challenge's day in the workspace zone, named in the reader's language.
  const dayOf = (value: string): string | null => {
    const day = calendarDayOf(value, timeZone);
    return day ? formatLearnerDay(day, locale) : null;
  };
  const ends = dayOf(challenge.endDate);
  const starts = dayOf(challenge.startDate);
  const percent = Math.round((Math.min(standing.current, standing.target) / standing.target) * 100);

  return (
    <Card className="rounded-xl border-border">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 min-w-0">
            <p className="font-semibold text-foreground" dir="auto">{challenge.name}</p>
            {challenge.description ? (
              <p className="text-sm text-muted-foreground" dir="auto">{challenge.description}</p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {starts ? t('portal.challenges.started', { date: starts }) : null}
              {starts && ends ? ' · ' : null}
              {ends ? t('portal.challenges.ends', { date: ends }) : null}
            </p>
          </div>
          {challenge.rewardPoints > 0 ? (
            <span className="flex items-center gap-1 text-sm font-semibold text-amber-600 dark:text-amber-400 shrink-0">
              <Trophy className="h-4 w-4" aria-hidden="true" />
              {t('portal.challenges.reward', { points: challenge.rewardPoints })}
            </span>
          ) : null}
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('portal.challenges.progress')}</span>
            <span>{`${Math.min(standing.current, standing.target)} / ${standing.target}`}</span>
          </div>
          <Progress
            value={percent}
            className="h-2"
            aria-label={t('portal.challenges.progressOf', { name: challenge.name })}
          />
        </div>

        <div className="flex justify-end">
          {completed ? (
            <Badge variant="secondary">{t('portal.challenges.completed')}</Badge>
          ) : standing.accepted ? (
            <Badge variant="outline">{t('portal.challenges.accepted')}</Badge>
          ) : (
            <Button size="sm" onClick={onAccept} disabled={accepting}>
              {t('portal.challenges.accept')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
